/**
 * Photo gallery.
 *
 * Renders the plain-image media the admin panel accepts. Without this, an
 * uploaded photo was stored correctly and displayed nowhere — the panel
 * offered three media types and the site rendered only two.
 *
 * `limit` keeps the homepage appearance short; Our Works shows everything with
 * category filters.
 */
import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Eyebrow from "../shared/Eyebrow";
import RevealText from "../shared/RevealText";
import { mediaService } from "../../services/mediaService";
import { useRemoteData } from "../../hooks/useRemoteData";
import AsyncSection from "../shared/AsyncSection";
import { cn } from "../../lib/utils";

export default function PhotoGallery({
  limit = null,
  showFilters = true,
  title = "Recent work,\nphotographed.",
  eyebrow = "Gallery",
}) {
  const [cat, setCat] = useState("All");

  const { data: media, loading } = useRemoteData(() => mediaService.list(), [], {
    initialValue: [],
  });

  const photos = useMemo(
    () => (media ?? []).filter((m) => m.type === "image" && m.active !== false),
    [media]
  );

  // Built from what has actually been uploaded rather than a fixed list, so a
  // filter never leads to an empty gallery.
  const categories = useMemo(() => {
    const used = new Set(
      photos.map((p) => p.serviceCategory).filter((value) => Boolean(value))
    );
    return ["All", ...Array.from(used).sort()];
  }, [photos]);

  const items = useMemo(() => {
    const filtered = photos.filter(
      (p) => cat === "All" || p.serviceCategory === cat
    );
    return limit ? filtered.slice(0, limit) : filtered;
  }, [photos, cat, limit]);

  // A homepage section with nothing in it is worse than no section: hide it
  // entirely rather than showing an empty-state box to a customer.
  if (!loading && photos.length === 0 && limit) return null;

  return (
    <section
      className="bg-ink py-24 md:py-32 border-t border-charcoal/50"
      data-testid="photo-gallery"
    >
      <div className="container-emilio">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-10">
          <div className="lg:col-span-7">
            <Eyebrow>{eyebrow}</Eyebrow>
            <RevealText
              as="h2"
              text={title}
              className="mt-4 font-display font-extrabold text-white text-display"
            />
          </div>

          {showFilters && categories.length > 2 && (
            <div className="lg:col-span-5 flex flex-wrap gap-2">
              {categories.slice(0, 6).map((c) => (
                <button
                  type="button"
                  key={c}
                  data-testid={`photo-filter-${c.replace(/\s+/g, "-").toLowerCase()}`}
                  onClick={() => setCat(c)}
                  className={cn(
                    "text-xs uppercase tracking-[0.18em] px-3 py-2 border transition-colors",
                    cat === c
                      ? "border-emerald text-emerald"
                      : "border-charcoal text-mutedtext hover:text-white hover:border-white/40"
                  )}
                >
                  {c}
                </button>
              ))}
            </div>
          )}
        </div>

        <AsyncSection
          loading={loading}
          isEmpty={items.length === 0}
          skeleton={
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4" aria-hidden="true">
              {[0, 1, 2, 3, 4, 5].map((index) => (
                <div
                  key={index}
                  className="aspect-[4/3] bg-charcoal/40 border border-charcoal animate-pulse"
                />
              ))}
            </div>
          }
          emptyTitle="No photos in this category yet"
          emptyMessage="Try another category, or check back soon."
        >
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((photo) => (
              <figure
                key={photo.id}
                className="group relative overflow-hidden border border-charcoal bg-charcoal/20"
                data-testid={`photo-${photo.id}`}
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={photo.imageUrl}
                    // Alt text is required by the admin panel, so this is
                    // meaningful rather than a filename.
                    alt={photo.altText || photo.title}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>

                {(photo.caption || photo.title) && (
                  <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-ink via-ink/80 to-transparent p-4 pt-10">
                    <div className="text-white text-sm font-display font-semibold">
                      {photo.caption || photo.title}
                    </div>
                    {photo.serviceCategory && (
                      <div className="mt-0.5 text-[11px] uppercase tracking-[0.16em] text-mutedtext">
                        {photo.serviceCategory}
                      </div>
                    )}
                  </figcaption>
                )}
              </figure>
            ))}
          </div>
        </AsyncSection>

        {/* On the homepage, point at the full gallery rather than showing it all. */}
        {limit && photos.length > limit && (
          <div className="mt-10">
            <Link
              to="/our-works"
              className="inline-flex items-center gap-2 text-emerald hover:text-white text-sm uppercase tracking-[0.16em]"
              data-testid="photo-gallery-more"
            >
              See all {photos.length} photos <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
